import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Loader2, Search, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { LoadingSpinner } from "@/components/loading-spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TemplateCard } from "@/components/templates/TemplateCard";
import { UseTemplateDialog } from "@/components/templates/UseTemplateDialog";
import { TemplatePreviewModal } from "@/components/templates/TemplatePreviewModal";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

export default function TemplateList() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<{
    _id: string;
    name: string;
    description?: string;
  } | null>(null);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Preview modal state
  const [previewTemplate, setPreviewTemplate] = useState<{
    _id: string;
    name: string;
    description?: string;
    typstContent: string;
  } | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Fetch user document templates from Convex
  const templates = useQuery(api.userDocTemplates.list);
  const deleteTemplateMutation = useMutation(api.userDocTemplates.remove);
  const subscriptionData = useQuery(api.subscriptions.getOrganizationSubscription);

  // Filter templates based on search query
  const filteredTemplates =
    templates?.filter(
      (template: { name: string; description?: string }) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  // Show loading state while fetching data
  if (templates === undefined) {
    return (
      <DashboardLayout activeTab="document-writer">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  const handleUseTemplate = (template: { _id: string; name: string; description?: string }) => {
    setSelectedTemplate(template);
    setUseDialogOpen(true);
  };

  const handlePreview = (template: {
    _id: string;
    name: string;
    description?: string;
    typstContent: string;
  }) => {
    setPreviewTemplate(template);
    setPreviewModalOpen(true);
  };

  // Handler for creating a template - checks if user is on free tier first
  const handleCreateTemplate = () => {
    const planType = subscriptionData?.organization?.planType;
    const isFree = !planType || planType === "basic";

    if (isFree) {
      setShowUpgradePrompt(true);
      return;
    }

    navigate("/document-writer", { state: { fromDocuments: true } });
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTemplateMutation({ templateId: templateToDelete as any });
      setTemplateToDelete(null);
    } catch (error) {
      console.error("Failed to delete template:", error);
      alert("Failed to delete template. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DashboardLayout activeTab="document-writer">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 pl-0"
                onClick={() => navigate("/documents")}
              >
                <ArrowLeft className="h-4 w-4" /> Back to Documents
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Document Templates</h1>
            <p className="mt-1 text-gray-500">
              Use your saved templates to quickly generate new documents
            </p>
          </div>
        </div>

        {/* Search and Filter Row */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search templates..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-16 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 p-4">
              <FileText className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">No templates yet</h3>
            <p className="mx-auto mb-6 max-w-md text-gray-500">
              Save a document as a template to use it as a starting point for new documents.
            </p>
            <Button onClick={handleCreateTemplate} className="gap-2">
              <FileText className="h-4 w-4" />
              Create Your First Template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map(
              (template: {
                _id: string;
                name: string;
                description?: string;
                createdAt: number;
                typstContent: string;
              }) => (
                <TemplateCard
                  key={template._id}
                  template={template}
                  onUseTemplate={handleUseTemplate}
                  onPreview={handlePreview}
                  onDelete={setTemplateToDelete}
                />
              )
            )}
          </div>
        )}
      </div>

      {/* Template Preview Modal */}
      <TemplatePreviewModal
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewTemplate(null);
        }}
        onUseTemplate={() => {
          if (previewTemplate) {
            handleUseTemplate(previewTemplate);
          }
        }}
        template={previewTemplate}
      />

      {/* Use Template Dialog */}
      <UseTemplateDialog
        isOpen={useDialogOpen}
        onClose={() => {
          setUseDialogOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!templateToDelete}
        onOpenChange={(open) => !open && setTemplateToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTemplate}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Prompt Dialog for Free Tier Users */}
      <UpgradePromptDialog
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        title="Save Unlimited Templates"
        description="Saving custom templates is a Professional feature. Upgrade to save time and standardize your documents."
        feature="Document Templates"
      />
    </DashboardLayout>
  );
}
